<template>
  <v-container fluid class="list-page" :class="{ 'list-page--embedded': isEmbedded }">
    <div v-if="!isEmbedded" class="list-page-heading">
      <div><div class="list-page-heading__eyebrow">Billing</div><h1>Open invoices</h1><p>Review unpaid balances, collection activity, and payment progress.</p></div>
    </div>

    <v-row v-if="!isEmbedded" class="list-metrics">
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-receipt-text-clock-outline</v-icon></div><div><div class="list-metric__value">{{ Invoices.length }}</div><div class="list-metric__label">Open invoices</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-cash-multiple</v-icon></div><div><div class="list-metric__value">{{ fullTotal | currency }}</div><div class="list-metric__label">Invoice total</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-cash-check</v-icon></div><div><div class="list-metric__value">{{ fullPayment | currency }}</div><div class="list-metric__label">Payments received</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-cash-clock</v-icon></div><div><div class="list-metric__value">{{ fullBalance | currency }}</div><div class="list-metric__label">Outstanding balance</div></div></div></v-col>
    </v-row>

    <v-card :loading="loading" elevation="0" class="list-card">
      <div v-if="isEmbedded" class="list-card-header"><div><div class="list-card-header__title">Open invoices</div><div class="list-card-header__subtitle">Outstanding invoices for this account</div></div><div class="embedded-invoice-summary"><span class="list-count">{{ Invoices.length }}</span><strong>{{ fullBalance | currency }} due</strong></div></div>
      <div class="list-toolbar">
        <v-text-field v-model="search" class="list-toolbar__search" outlined prepend-inner-icon="mdi-magnify" dense background-color="#F8FAF8" placeholder="Search invoice number, name, or amount" single-line hide-details clearable />
        <div class="list-toolbar__actions">
          <v-btn-toggle v-if="toggleStatus === 'show' && !isEmbedded" v-model="toggleChange" mandatory dense class="open-invoice-filter">
            <v-btn small value="all"><v-icon left small>mdi-format-list-bulleted</v-icon>All</v-btn>
            <v-btn small value="donors"><v-icon left small>mdi-hand-coin-outline</v-icon>Donors</v-btn>
            <v-btn small value="parents"><v-icon left small>mdi-account-outline</v-icon>Parents</v-btn>
          </v-btn-toggle>
          <v-btn icon outlined color="primary" :loading="loading" aria-label="Refresh open invoices" @click="getList"><v-icon size="19">mdi-refresh</v-icon></v-btn>
        </div>
      </div>

      <v-data-table class="list-table list-table--responsive" :headers="headers" :items="Invoices" :search="search"
          :sort-desc="sortDesc" :loading="loading" :sort-by="sortBy" :page.sync="page" :items-per-page="itemsPerPage">
          <template v-slot:item="{ item }">
            <tr class="list-table-row">
              <td data-label="Created" class="text-left pointer">
                <div class="list-cell-primary">{{ item.date | moment("M/D/YYYY") }}</div>
                <v-tooltip bottom v-if="item.due !== 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" small icon class="ma-0 pa-0" @click="setAutoCollect(item)">
                      <v-icon color="error" v-if="item.collectionStatus === 'exhausted'">mdi-alert-circle</v-icon>
                      <v-icon class="green--text" v-else-if="item.autoCollect">mdi-check-circle</v-icon>
                      <v-icon v-else >mdi-check-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span v-if="item.collectionStatus === 'exhausted'">Resume Auto Collect</span>
                  <span v-else-if="item.autoCollect">Disable Auto Collect</span>
                  <span v-else>Enable Auto Collect</span>
                </v-tooltip>
              </td>
              <td data-label="Invoice" class="text-left">
                <button type="button" class="open-invoice-number" @click="viewInvoice(item)">#{{ item.localId }}</button>
                <v-chip v-if="hasOnlinePayment(item)" class="ml-1" small label color="green lighten-5" text-color="green darken-2">Online</v-chip>
              </td>
              <!-- <td class="text-left pointer" @click="viewInvoice(item)">
                {{ item.localId }}
                <v-chip v-if="item.due === 0" small color="red" text-color="white" class="ml-2 font-weight-bold" label>
                  Paid
                </v-chip>
              </td> -->
              <td data-label="Account" class="text-left pointer" v-if="!DonorId && !CustomerId">
                <div class="list-identity" @click="viewProfileNewTab(item)"><div class="list-identity__icon"><v-icon color="primary" size="19">{{ item.Customer ? "mdi-account-outline" : "mdi-hand-coin-outline" }}</v-icon></div><div class="list-identity__content"><div class="list-identity__title">{{ item.name }}</div><div class="list-identity__subtitle">{{ item.Customer ? "Parent" : "Donor" }}</div></div></div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" v-if="(item.Customer && item.Customer.deletedAt) || (item.Donor && item.Donor.deletedAt)" color="error" size="17">mdi-account-off-outline</v-icon>
                  </template>
                  <span v-if="item.Customer">Customer is deleted</span>
                  <span v-if="item.Donor">Donor is deleted</span>
                </v-tooltip>
              </td>
              <td data-label="Due date" class="text-left pointer" @click="viewInvoice(item)">
                <div class="list-cell-primary">{{ item.dueDate | moment("M/D/YYYY") }}</div>
                <div class="list-cell-secondary">{{ item.dueDate | moment("from", "now") }}</div>
              </td>
              <td data-label="Transactions" class="text-center" @click="item.transactionCount ? openTransaction(item) : undefined">
                <button type="button" class="transaction-count" :class="{ 'transaction-count--active': item.transactionCount }">{{ item.transactionCount || 0 }}</button>
              </td>
              <td data-label="Total" class="text-right pointer" @click="viewInvoice(item)">
                <span class="open-invoice-amount">{{ item.total | currency }}</span>
              </td>
              <td data-label="Paid" class="text-right pointer" @click="viewInvoice(item)">
                <span class="open-invoice-payment">{{ item.payment | currency }}</span>
              </td>
              <td data-label="Balance" class="text-right pointer" @click="viewInvoice(item)">
                <span class="open-invoice-balance">{{ item.due | currency }}</span>
              </td>
              <td data-label="Actions" class="justify-end align-center text-right pointer action-cell">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list class="menu-button">
                    <v-list-item @click="viewInvoice(item)">
                      <v-list-item-title>View Invoice</v-list-item-title>
                    </v-list-item>


                    <v-list-item v-if="item.due > 0" @click="receivePayment(item)">
                      <v-list-item-title>Receive Payment</v-list-item-title>
                    </v-list-item>
                    <!-- <v-list-item @click="viewPackingSlip(item)">
                        <v-list-item-title>View Packing Slip</v-list-item-title>
                      </v-list-item> -->
                    <v-divider v-if="!DonorId && !CustomerId"></v-divider>
                    <v-list-item v-if="!DonorId && !CustomerId" @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <!-- <v-list-item v-if="item.payment > 0" @click="refundInvoice(item)">
                      <v-list-item-title>Refund</v-list-item-title>
                    </v-list-item> -->
                    <v-list-item v-if="checkRightStatus(59)" @click="editInvoice(item)">
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.due !== 0 && checkRightStatus(60)" @click="deleteInvoice(item)">
                      <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                    <ShareLink :id="item.id" :postfix="`view?key=${currentBusiness.code}`" type="invoice"
                      :text="`${currentBusiness.name} Invoice click here to view`" />
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
          <template v-slot:no-data><div class="list-empty-state py-12"><v-icon size="48" color="grey lighten-1">mdi-receipt-text-check-outline</v-icon><strong>No open invoices</strong><span>There are no outstanding invoices for this selection.</span></div></template>
          <template v-slot:no-results><div class="list-empty-state py-12"><v-icon size="48" color="grey lighten-1">mdi-receipt-text-search-outline</v-icon><strong>No invoices found</strong><span>No open invoices matched “{{ search }}”.</span></div></template>
        </v-data-table>
    </v-card>
    <v-dialog v-model="loadingDialog" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-container>
          <div class="mb-3">Please stand by deleting Invoice</div>
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-container>
      </v-card>
    </v-dialog>

    <TransactionDialog :openTransactionDialog="openTransactionDialog" @close="openTransactionDialog = false"
      :selectedInvoice="selectedInvoice" />

    <InvoicePaymentReceive @close="closePayment" :openReceivePaymentDialog="openReceivePaymentDialog"
      :Invoice="selectedInvoice"></InvoicePaymentReceive>

    <v-dialog v-model="refundDialog" width="500" persistent>

      <v-card v-if="selectedInvoice">
        <ModelTitle :title="`Refund Invoice # ${selectedInvoice.localId}`" @close="close()" />

        <v-container grid-list-lg>

          <div class="text-center">
            <v-btn-toggle small dense v-model="paymentType" color="primary">

              <v-btn outlined small value="manual">
                Manual
              </v-btn>
              <v-btn outlined small value="online">
                Online
              </v-btn>


            </v-btn-toggle>
          </div>

          <div class="text-center mt-4">
            <div>Invoice Due: <span>{{ selectedInvoice.due | currency }}</span></div>
            <div>Total Payment: <span>{{ selectedInvoice.payment | currency }}</span></div>
          </div>


          <v-layout row class="mt-5" justify-center v-if="paymentType === 'manual'">
            <v-flex xs6>
              <DateSelector :initValue="Payment.date" label="Payment Date" v-model="Payment.date" />
            </v-flex>
            <v-flex xs6>
              <v-select dense outlined :items="paymentMethods" v-model="Payment.paymentMethod" label="Payment Method"
                item-text="name" hide-details return-object></v-select>
            </v-flex>
            <v-flex xs6>
              <v-text-field outlined dense hide-details v-model="Payment.referenceNumber"
                label="Reference Number"></v-text-field>
            </v-flex>
            <v-flex xs6>

              <v-text-field class="al-r" outlined dense hide-details v-model="Payment.amount"
                label="Amount"></v-text-field>
            </v-flex>

          </v-layout>

          <v-layout row class="mt-5" justify-center v-if="paymentType === 'online'">
            <v-flex xs12>
              <DateSelector :initValue="Payment.date" label="Payment Date" v-model="Payment.date" />
            </v-flex>
            <v-flex xs8>
              <v-select item-text="last4" return-object v-model="selectedCard" :loading="loadingCard" outlined dense
                hide-details :items="cards" label="Select Card">
                <template slot="item" slot-scope="{ item }">
                  <span>{{ item.last4 }}</span>
                  <v-spacer></v-spacer>
                  <span class="text-capitalize">{{ item.payment_method_type }}</span>
                </template></v-select>
            </v-flex>
            <v-flex xs4>

              <v-text-field class="al-r" outlined dense hide-details v-model="Payment.amount"
                label="Amount"></v-text-field>
            </v-flex>

          </v-layout>
        </v-container>


        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="close()">Close</v-btn>
          <v-btn color="primary" :loading="loadingBtn" dark @click="refund()">Refund</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import invoiceService from "@/modules/Invoice/service.js";
import ShareLink from "@/components/ShareLink";
// const DateTimePicker = () => import("@/components/DateTimePicker");
// import paymentService from "@/modules/Customer/Payment/service.js";

import Hashids from "hashids";
const hashids = new Hashids();

// import stripeService from "@/modules/Stripe/service.js";
// import DateSelector from "@/components/DateSelector";
import payCompassService from "@/modules/PayCompass/service.js";
import TransactionDialog from "@/modules/Invoice/components/Transaction.vue";
const InvoicePaymentReceive = () => import("@/modules/Invoice/Payment/components/InvoicePaymentCreate.vue");

export default {
  props: {
    toggleStatus: {
      default: 'show',
    },
    CustomerId: {
      default: null,
    },
    DonorId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
  },
  name: "invoice-list",
  data() {
    return {
      paymentMethods: [
        'Stripe',
        'Mohid',
        "Cash",
        "Check",
        "Paypal",
        "Debit Card",
        "Credit Card",
        "Wire",
        "Quick Pay",
        "Cash App",
        "Exchange",
        "Masjid",
        "Other",
      ],
      paymentType: 'manual',
      openTransactionDialog: false,
      showCustomersOnly: false,
      showDonorsOnly: false,
      toggleChange: 'all',
      Payment: {
        CustomerId: 0,
        date: this.$moment().toISOString(),
        referenceNumber: null,
        donationReferenceNumber: null,
        paymentMethod: "Stripe",
        donationPaymentMethod: "Masjid",
        PaymentDetails: [],
        amount: 0,
        donationAmount: 0,
        donation: false,
        parent: true,
      },
      openReceivePaymentDialog: false,
      loadingBtn: false,
      refundDialog: false,
      selectedInvoice: null,
      cards: [],
      amount: 0,
      paymentDialog: false,
      loadingDialog: false,
      fullTotal: 0,
      fullPayment: 0,
      fullBalance: 0,
      fab: false,
      dateTime: {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      createDialog: false,
      selectedPayee: null,
      headers: [],
      Invoices: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "date",
      pageCount: 0,
      sortDesc: true,
      itemsPerPage: 10,
    };
  },
  watch: {
    paymentType(val) {
      console.log('val', val, this.Payment)
      if (val === 'online') {
        this.Payment.date = this.$moment().toISOString()
      } else {
        console.log('here in munal', this.selectedInvoice)
        this.Payment.date = this.$moment(this.selectedInvoice.date).toISOString()
      }

    },
    paymentDialog(val) {
      if (val) {
        if (this.selectedInvoice && this.selectedInvoice.Customer && this.selectedInvoice.Customer.payCompassId) {
          this.getCard(this.selectedInvoice.Customer.payCompassId)
        }
      }
    },
    refundDialog(val) {
      if (val) {
        if (this.selectedInvoice && this.selectedInvoice.Customer && this.selectedInvoice.Customer.payCompassId) {
          this.getCard(this.selectedInvoice.Customer.payCompassId)
        }
      }
    },
    DonorId: {
      immediate: true,
      handler() {
        this.getList();
      },
    },
    toggleChange(val){
      this.showDonorsOnly = val === 'donors'
      this.showCustomersOnly = val === 'parents'
      this.getList()
    },
  },
  created() {
    this.loadHeader();
    // this.$events.listen("invoiceListEvent", () => this.getList());
    // document.title = "taleem - Invoice List";
  },
  components: {
    InvoicePaymentReceive,
    // DateTimePicker,
    // DateSelector,
    ShareLink,
    TransactionDialog
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    ...mapGetters("customer", ["getCustomer"]),
    ...mapGetters("invoice", ["InvoiceDetails", "getEditInvoiceId"]),
    fullFlexSize() {
      if (this.Payment.donation && this.Payment.parent)
        return true
      return false
    },
    isEmbedded() {
      return Boolean(this.CustomerId || this.DonorId)
    },
  },
  methods: {
    ...mapActions("invoice", ["ResetInvoice"]),
    closePayment() {
      this.openReceivePaymentDialog = false;
      this.getList()
    },
    setAutoCollect(invoice) {
      this.loading = true
      const autoCollect = invoice.collectionStatus === 'exhausted' ? true : !invoice.autoCollect
      invoiceService.update(invoice.id, { autoCollect }).then((response) => {
        if (response.data) {
          invoice.autoCollect = autoCollect
          invoice.collectionStatus = autoCollect ? 'pending' : 'disabled'
          this.loading = false
        }
      }).catch((error) => {
        this.$swal
        .mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        })
        .fire({
          icon: "error",
          title: error.data.message,
        });
        this.loading = false
      });
    },
    viewCustomerProfile(val) {
      window.open(`/parents/${val.id}/profile`, '_blank');
      // this.$router.push({ path: `/parents/${val.id}/profile` });
    },
    viewDonorProfile(val) {
      window.open(`/donors/${val.id}/profile`, '_blank');
      // this.$router.push({ path: `/parents/${val.id}/profile` });
    },
    reset() {
      this.Payment = {
        CustomerId: 0,
        date: this.$moment().toISOString(),
        referenceNumber: null,
        paymentMethod: "Stripe",
        PaymentDetails: [],
        amount: 0,
        donation: false,
        parent: true,
      }
      this.paymentType = 'manual'
    },
    close() {
      this.reset()
      this.paymentDialog = false
      this.refundDialog = false
    },
    hasOnlinePayment(invoice) {
      return Boolean(
        (invoice.Customer && invoice.Customer.payCompassId) ||
        (invoice.Donor && invoice.Donor.payCompassId)
      )
    },
    refund() {
      if (!this.selectedCard && this.paymentType === 'online') {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Select card on file",
          });
        return false
      }
      if (this.Payment.amount > 0) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Refund amount can't be greater than 0",
          });
        return false
      }
      if ((this.Payment.amount * -1) > this.selectedInvoice.payment) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "You can't refund amount more than payment",
          });
        return false
      }
      this.loadingBtn = true
      this.Payment.CustomerId = this.selectedInvoice.CustomerId
      this.Payment.PaymentDetails.push({
        amount: this.Payment.amount,
        referenceNumber: this.Payment.referenceNumber,
        InvoiceId: this.selectedInvoice.id,
        donation: false
      })
      if (this.paymentType === 'online') {
        this.refundPayment()
      } else {
        this.savePayment()
      }
    },
    receivePayment(val) {
      this.selectedInvoice = val
      this.openReceivePaymentDialog = !this.openReceivePaymentDialog
    },
    openTransaction(val) {
      this.selectedInvoice = val
      this.openTransactionDialog = !this.openTransactionDialog
    },
    async receivePayment1(val) {

      this.selectedInvoice = (await invoiceService.getById(val.id)).data
      console.log('payment receive', val, this.selectedInvoice)
      let dueAmount = 0
      // this.selectedInvoiceParentPayDue = dueAmount
      this.selectedInvoice.InvoiceDetails.map(row => {
        dueAmount += row.parentPay
      })
      if (dueAmount === 0) {
        this.Payment.amount = val.due
        this.Payment.donationAmount = this.selectedInvoice.due - val.due
        this.selectedInvoiceParentPayDue = val.due
      } else {
        this.Payment.amount = dueAmount
        this.Payment.donationAmount = this.selectedInvoice.due - dueAmount
        this.selectedInvoiceParentPayDue = dueAmount
      }
      this.Payment.date = this.$moment(val.date).toISOString()
      this.paymentDialog = !this.paymentDialog
    },
    async refundInvoice(val) {
      this.selectedInvoice = (await invoiceService.getById(val.id)).data
      console.log('payment refund', val, this.selectedInvoice)

      this.Payment.amount = this.selectedInvoice.payment * -1

      this.Payment.date = this.$moment(val.date).toISOString()
      this.refundDialog = true;
    },
    refundPayment() {
      payCompassService.creditAmount({
        amount: this.Payment.amount * -1,
        description: `Invoice #${this.selectedInvoice.localId}, IntId: ${this.selectedInvoice.id}`,
        source: this.selectedCard.id,
        customer: this.selectedInvoice.Customer.payCompassId,
        cardId: this.selectedCard.id
      }).then(response => {
        console.log('response payment', response)
        if (response && response.data && response.data.status_code === 'A') {
          this.Payment.referenceNumber = `${this.selectedCard.payment_method_type} - ${this.selectedCard.last4}`
          this.Payment.stripeChargeId = response.data.reference_number
          this.Payment.donation = false
          this.Payment.parent = true
          this.Payment.PaymentDetails[0].metadata = response.data
          this.$swal(response.data.status, 'Payment is refunded', 'success')
          this.savePayment()
        } else {
          this.$swal(response.data.status, response.data.error_message, 'error')
          this.loadingBtn = false
          return false
        }

      }).catch(error => {
        console.log('error stripe', error)
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: error.data.message,
          });
      })
    },

    // viewOpenInvoice() {
    //   const hashId = hashids.encode(this.DonorId);
    //   const routeData = this.$router.resolve({
    //     path: `/global/invoice/${hashId}/openInvoice`,
    //   });
    //   window.open(routeData.href, "newwindow", "width=850, height=1100");
    // },

    viewProfile(val) {
      if (val.DonorId) {
        this.$router.push({ path: `/donors/${val.DonorId}/profile` });
      } else {
        this.$router.push({ path: `/parents/${val.CustomerId}/profile` });
      }

    },
    viewProfileNewTab(val) {
      let routeData = null
      if (val.DonorId) {
        routeData = this.$router.resolve({
          path: `/donors/${val.DonorId}/profile`,
        });
      } else {
        routeData = this.$router.resolve({
          path: `/parents/${val.CustomerId}/profile`,
        });
      }
      window.open(routeData.href, "_blank");
    },
    viewInvoice(invoice) {
      if (invoice.DonorId) {
        this.donorInvoice(invoice)
      } else {
        this.openInvoice(invoice)
      }
    },
    openInvoice(invoice) {
      console.log('invoice', invoice.id)
      const hashId = hashids.encode(invoice.id);
      console.log('dueAmount', `localhost:8082/global/invoice/${hashId}/view`)
      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    donorInvoice(invoice) {
      console.log('invoice', invoice.id)
      const hashId = hashids.encode(invoice.id);
      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/donorInvoice`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    viewPackingSlip(invoice) {
      const hashId = hashids.encode(invoice.id);
      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/packingSlip`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    loadHeader() {
      this.headers = [];
      this.headers.push({
        text: this.$t("labels.date"),
        align: "left",
        value: "date",
        sortable: true,
      });

      this.headers.push({
        text: "Invoice Number",
        align: "left",
        value: "localId",
        sortable: true,
      });

      if (!this.DonorId && !this.CustomerId) {
        this.headers.push({
          text: "Customer Name",
          value: "name",
          align: "left",
          sortable: true,
        });
      }

      this.headers.push({
        text: "Due Date",
        align: "left",
        value: "dueDate",
        sortable: true
      });

      this.headers.push({
        text: "Transactions",
        align: "center",
        value: "transactionCount",
        sortable: true
      });

      this.headers.push({
        text: "Total",
        value: "total",
        align: "right",
        sortable: true,
      });

      this.headers.push({
        text: "Payment",
        value: "payment",
        align: "right",
        sortable: true,
      });

      this.headers.push({
        text: "Balance",
        value: "due",
        align: "right",
        sortable: true,
      });
      this.headers.push({
        text: "",
        value: "action",
        sortable: false,
      });
    },
    getList() {
      this.loading = true;
      let where = {};
      if (!this.showAll) {
        where = {
          due: true
        };
      }
      if (this.DonorId) {
        where.DonorId = this.DonorId;
      }

      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      if (this.due) {
        where.due = true;
      }
      if (this.$route.query.collectionStatus) {
        where.collectionStatus = this.$route.query.collectionStatus;
      }
      where.showCustomersOnly = this.showCustomersOnly;
      where.showDonorsOnly = this.showDonorsOnly;
      return invoiceService.getAll(where).then((response) => {
        this.Invoices = response.data || [];
        this.fullTotal = 0;
        this.fullPayment = 0;
        this.fullBalance = 0;
        this.Invoices.forEach((row) => {
          this.fullTotal += Number(row.total || 0);
          this.fullPayment += Number(row.payment || 0);
          this.fullBalance += Number(row.due || 0);
          if(row.Customer)
            row.name = row.Customer.name
          else if(row.Donor)
            row.name = row.Donor.name
        });
        return response;
      }).finally(() => {
        this.loading = false;
      })
    },
    editInvoice(invoice) {
      // console.log('ddf', invoice)
      this.$router.push({ path: `/invoice/${invoice.id}/edit` });
    },
    deleteInvoice(invoice) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.loadingDialog = true;
          invoiceService
            .delete(invoice.id)
            .then((response) => {
              console.log("response delete", response);
              this.loadingDialog = false;
              if (response.data) {
                this.$swal
                  .mixin({
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                  })
                  .fire({
                    icon: "success",
                    title: "Invoice has been deleted.",
                  });
                // this.$swal("Deleted!", "Invoice has been deleted.", "success");
                this.getList();
                this.$events.emit("customerProfileEvent");
              }
            })
            .catch((error) => {
              this.loadingDialog = false;
              this.$swal({
                title: "Delete Invoice",
                text: error.data.message,
                icon: "error",
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.open-invoice-filter {
  overflow: hidden;
  border: 1px solid #dbe4db;
  border-radius: 9px;
  box-shadow: none;
}

.embedded-invoice-summary { display: flex; align-items: center; gap: 10px; }
.embedded-invoice-summary strong { color: #2e7d32; font-size: 12px; }

.open-invoice-filter .v-btn {
  height: 38px !important;
  border: 0 !important;
  color: #667269;
  background: white !important;
  font-size: 11px;
  letter-spacing: 0;
  text-transform: none;
}

.open-invoice-filter .v-btn--active {
  color: #2e7d32 !important;
  background: #eaf5eb !important;
}

.open-invoice-number {
  padding: 0;
  border: 0;
  color: #2e7d32;
  background: transparent;
  font-weight: 800;
  cursor: pointer;
}

.transaction-count {
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 8px;
  color: #748079;
  background: #f0f3f0;
  font-weight: 750;
}

.transaction-count--active {
  color: #2e7d32;
  background: #e8f5e9;
  cursor: pointer;
}

.open-invoice-amount,
.open-invoice-payment,
.open-invoice-balance {
  color: #273247;
  font-weight: 700;
  white-space: nowrap;
}

.open-invoice-payment { color: #607067; }
.open-invoice-balance { color: #2e7d32; font-size: 13px; }

@media (max-width: 700px) {
  .open-invoice-filter .v-btn {
    min-width: 40px !important;
    padding: 0 10px !important;
    font-size: 0;
  }

  .open-invoice-filter .v-icon {
    margin: 0 !important;
  }
}
</style>
